import React from 'react';

import PropTypes from 'prop-types';
import {useSnackbar} from 'notistack';
import {Form, FormikProps} from 'formik';
import {CallMade} from '@material-ui/icons';
import {Box, Grid} from '@material-ui/core';

import {
    View,
    EfeResponse,
    SepomexCatalog,
    EfeCatalogValue,
    ScreenFilterValues,
    AddressServiceRequest,
    SepomexCatalogResponse,
} from 'commons/models';
import {
    Roles,
    Status,
    Messages,
    PersonTypes,
    ViewChildId,
    SnackbarVariants,
} from 'commons/constants';
import {FieldShape} from 'commons/shapes';
import {useAlert, useSpinner} from 'hooks';
import {InferPropTypes} from 'commons/types/Types';
import {CatalogServices, LayoutServices} from 'services';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';
import {EfeButtonPrimary, EfeFormSection, EfeSupportMessage} from 'components';

InnerFormAddress.propTypes = {
    viewId: PropTypes.number.isRequired,
    viewKey: PropTypes.string.isRequired,
    fieldHandler: PropTypes.func.isRequired,
    foreignCitizen: PropTypes.bool.isRequired,
    fields: PropTypes.arrayOf(PropTypes.shape(FieldShape).isRequired).isRequired,
};

function InnerFormAddress(
    props: InferPropTypes<typeof InnerFormAddress.propTypes & FormikProps<AddressServiceRequest>>
) {
    const {
        values,
        errors,
        fields,
        fieldHandler,
        viewId,
        viewKey,
        isSubmitting,
        setFieldValue,
        foreignCitizen,
    } = props;

    const {enqueueSnackbar} = useSnackbar();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();

    function resetValues() {
        setFieldValue('municipalityKey', '');
        setFieldValue('municipalityDescription', '');
        setFieldValue('stateKey', '');
        setFieldValue('stateDescription', '');
        setFieldValue('countryKey', '');
    }

    function setCatalogValues(
        data: Array<SepomexCatalog>,
        zipCode: string,
        colonyKey: number,
        stateKey: number
    ) {
        const colony: Array<EfeCatalogValue> = [];
        const state: Array<EfeCatalogValue> = [];
        let colonyItem: EfeCatalogValue;
        let stateItem: EfeCatalogValue;
        data.map((item: SepomexCatalog, index: number) => {
            if (!colonyItem || colonyItem.id !== item.settlementCpconsId) {
                colonyItem = {
                    id: item.settlementCpconsId,
                    order: index,
                    value: item.settlement,
                    status: Status.Active,
                };
                colony.push({...colonyItem});
            }
            if (!stateItem || stateItem.id !== item.stateKey) {
                stateItem = {
                    id: item.stateKey,
                    order: index,
                    value: item.state,
                    status: Status.Active,
                };
                state.push({...stateItem});
            }
        });
        if (fields && fields.length) {
            fields[colonyKey].catalogValues = [...colony];
            fields[stateKey].catalogValues = [...state];
            if (data && data.length > 0) {
                setValues(data[0], zipCode);
            }
        }
    }
    function setValues(data: SepomexCatalog, zipCode: string) {
        if (zipCode) {
            setFieldValue('zipCode', zipCode);
        }
        if (data && data.municipalityKey) {
            setFieldValue('municipalityKey', data.municipalityKey);
        }
        if (data && data.municipality) {
            setFieldValue('municipalityDescription', data.municipality);
        }
        if (data && data.stateKey) {
            setFieldValue('stateKey', data.stateKey);
        }
        if (data && data.state) {
            setFieldValue('stateDescription', data.state);
        }
    }

    async function searchZipCode(value: string, colonyKey: number, stateKey: number) {
        if (value && value.length === 5) {
            setIsLoading(true);
            try {
                const sepomexData: EfeResponse<SepomexCatalogResponse> =
                    await CatalogServices.getSepomexCatalog(value);
                if (sepomexData.data && sepomexData.data.data && sepomexData.data.data.length > 0) {
                    setCatalogValues([...sepomexData.data.data], value, colonyKey, stateKey);
                } else {
                    setCatalogValues([], value, colonyKey, stateKey);
                    resetValues();
                    enqueueSnackbar(`${Messages.SEPOMEX_ERROR}${value}`, {
                        variant: SnackbarVariants.ERROR,
                    });
                }
                setIsLoading(false);
            } catch (error) {
                resetValues();
                setIsLoading(false);
                setCatalogValues([], value, colonyKey, stateKey);
                enqueueSnackbar(`${Messages.SEPOMEX_ERROR}${value}`, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        }
    }

    React.useEffect(() => {
        const colonyKey = fields.findIndex((field) => field.attrName === 'colonyKey');
        const stateKey = fields.findIndex((field) => field.attrName === 'stateKey');
        if (
            values.zipCode &&
            values.zipCode.length === 5 &&
            !foreignCitizen &&
            colonyKey >= 0 &&
            stateKey >= 0
        ) {
            searchZipCode(values.zipCode, colonyKey, stateKey);
        }
    }, [values.zipCode, fields]);

    React.useEffect(() => {
        if (foreignCitizen && viewKey === ViewChildId.BENEFICIARY_ADDRESS) {
            (async function () {
                try {
                    setIsLoading(true);
                    const request: ScreenFilterValues = {
                        viewId: viewId,
                        personType: PersonTypes.FISICA,
                        foreign: foreignCitizen,
                        address: true,
                        rol: Roles.BENEFICIARY,
                        viewKey: ViewChildId.BENEFICIARY_ADDRESS,
                    };
                    const response: EfeResponse<View> = await LayoutServices.getViewByFilters(
                        request
                    );
                    if (response.data && response.data.fields) {
                        setIsLoading(false);
                        fieldHandler(response.data);
                    } else {
                        enqueueSnackbar(Messages.LAYOUT_ERROR, {
                            variant: SnackbarVariants.ERROR,
                        });
                        fieldHandler(undefined);
                        setIsLoading(false);
                    }
                } catch (error) {
                    setIsLoading(false);
                    fieldHandler(undefined);
                    showAlertError(error, Messages.LAYOUT_ERROR);
                }
            })();
        } else {
            fieldHandler(undefined);
        }
    }, [foreignCitizen]);

    /* eslint-disable react/jsx-handler-names */
    return (
        <Form noValidate>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            {fields && fields.length > 0 && (
                <Box m={2}>
                    <EfeSupportMessage />
                    <EfeFormSection
                        fields={fields}
                        values={values}
                        errors={errors}
                        setFieldValue={setFieldValue}
                    />
                    <Grid container direction='row' justifyContent='flex-end'>
                        <Box pt={6} width={160}>
                            <EfeButtonPrimary
                                fullWidth
                                disabled={isSubmitting}
                                type='submit'
                                endIcon={<CallMade />}>
                                {'OK'}
                            </EfeButtonPrimary>
                        </Box>
                    </Grid>
                </Box>
            )}
        </Form>
    );
}

export default InnerFormAddress;
